/**
 * @fileoverview NotificationCenter component for displaying user notifications
 * 
 * @purpose Provides a notification center UI component that displays user notifications
 *          with real-time updates. Includes unread count badge and mark-as-read functionality.
 * 
 * @usage Import in app components (e.g., top bar):
 *        import { NotificationCenter } from '@korvo/core/components/shared'
 * 
 * @ownership Core infrastructure - notification UI
 */

'use client';
import { useCallback, useEffect, useState } from 'react';

import { useSession } from '../../lib/better-auth/client';
import { type NormalizedError, normalizeError } from '../../lib/normalize-error';
import type { Notification } from '../../lib/notifications-types';

type NotificationsResponse = {
    notifications: Notification[];
    unreadCount: number;
};

export function NotificationCenter() {
    const { data: session } = useSession();
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [isOpen, setIsOpen] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    
    const fetchNotifications = useCallback(async () => {
        if (!session?.user?.id)
            return;
        setIsLoading(true);
        try {
            const response = await fetch('/api/notifications');
            if (response.ok) {
                const data = (await response.json()) as NotificationsResponse;
                setNotifications(Array.isArray(data.notifications) ? data.notifications : []);
                setUnreadCount(typeof data.unreadCount === 'number' ? data.unreadCount : 0);
            }
        }
        catch (error: unknown) {
            const errorNormalized: NormalizedError = normalizeError(error);
            console.errorNormalized('Failed to fetch notifications:', errorNormalized);
        }
        finally {
            setIsLoading(false);
        }
    }, [session?.user?.id]);
    
    useEffect(() => {
        if (session?.user?.id) {
            void fetchNotifications();
            // Poll for new notifications every 30 seconds
            const interval = setInterval(() => {
                void fetchNotifications();
            }, 30000);
            return () => clearInterval(interval);
        }
    }, [session?.user?.id, fetchNotifications]);
    
    const markAsRead = async (notificationId: string) => {
        try {
            await fetch(`/api/notifications/${notificationId}/read`, {
                method: 'POST'
            });
            setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, read: true } : n));
            setUnreadCount(prev => Math.max(0, prev - 1));
        }
        catch (error: unknown) {
            const errorNormalized: NormalizedError = normalizeError(error);
            console.errorNormalized('Failed to mark notification as read:', errorNormalized);
        }
    };
    
    const markAllAsRead = async () => {
        try {
            await fetch('/api/notifications/read-all', {
                method: 'POST'
            });
            setNotifications(prev => prev.map(n => ({ ...n, read: true })));
            setUnreadCount(0);
        }
        catch (error: unknown) {
            const errorNormalized: NormalizedError = normalizeError(error);
            console.errorNormalized('Failed to mark all notifications as read:', errorNormalized);
        }
    };
    
    if (!session)
        return null;
        
    return (<div className="relative">
      {/* Notification Bell */}
      <button onClick={() => setIsOpen(!isOpen)} className="relative p-2 text-gray-600 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-full">
        <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"/>
        </svg>
        {unreadCount > 0 && (<span className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>)}
      </button>

      {/* Notification Dropdown */}
      {isOpen && (<div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
          <div className="p-4 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900">Notifications</h3>
              {unreadCount > 0 && (<button onClick={() => { void markAllAsRead(); }} className="text-sm text-blue-600 hover:text-blue-800">
                  Mark all as read
                </button>)}
            </div>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {isLoading ? (<div className="p-4 text-center text-gray-500">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-2">Loading notifications...</p>
              </div>) : notifications.length === 0 ? (<div className="p-4 text-center text-gray-500">
                <svg className="h-12 w-12 mx-auto text-gray-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"/>
                </svg>
                <p>No notifications</p>
              </div>) : (<div className="divide-y divide-gray-200">
                {notifications.map((notification) => (<div key={notification.id} className={`p-4 hover:bg-gray-50 cursor-pointer ${!notification.read ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''}`} onClick={() => {
                        if (!notification.read) {
                            void markAsRead(notification.id);
                        }
                    }}>
                    <div className="flex items-start">
                      <div className={`flex-shrink-0 w-2 h-2 rounded-full mt-2 ${notification.type === 'error' ? 'bg-red-500' :
                        notification.type === 'warning' ? 'bg-yellow-500' :
                            notification.type === 'success' ? 'bg-green-500' :
                                'bg-blue-500'}`}></div>
                      <div className="ml-3 flex-1">
                        <p className={`text-sm font-medium ${!notification.read ? 'text-gray-900' : 'text-gray-700'}`}>
                          {notification.title}
                        </p>
                        <p className="text-sm text-gray-600 mt-1">
                          {notification.message}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(notification.createdAt).toLocaleString()}
                        </p>
                      </div>
                      {!notification.read && (<div className="flex-shrink-0">
                          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        </div>)}
                    </div>
                  </div>))}
              </div>)}
          </div>
        </div>)}
    </div>);
}
